import React, { useState } from 'react';
import { Offcanvas, OffcanvasHeader, OffcanvasBody } from 'reactstrap';
import { toast } from 'react-toastify';
import { useTenant } from '../../../../hooks/useTenant';
import TaskService from '../../../../services/task_service';
import {
    TASK_PRIORITY,
    CONTACT_REASON_LABELS,
    CONTACT_REASON_ICONS
} from '../../../../constants/tasks';
import MarkdownViewer from '../../../../components/Common/MarkdownViewer';
import NeoCard from '../../../../components/Common/NeoCard';
import NeoBadge from '../../../../components/Common/NeoBadge';
import NeoButton from '../../../../components/Common/NeoButton';

// Subcomponents
import SidebarMemberInfo from './SidebarSections/SidebarMemberInfo';
import SidebarConsultantInfo from './SidebarSections/SidebarConsultantInfo';
import SidebarAICoachChat from './SidebarSections/SidebarAICoachChat';
import SidebarRegisterOutcome from './SidebarSections/SidebarRegisterOutcome';
import SidebarContactHistory from './SidebarSections/SidebarContactHistory';
import SidebarCardNotes from './SidebarSections/SidebarCardNotes';
import SidebarHistoryModal from './SidebarSections/SidebarHistoryModal';

const ATTEMPT_CONFIG = {
    0: { label: '1ª tentativa', hint: 'Primeiro contato — apresente-se e ofereça ajuda.', color: 'primary' },
    1: { label: '2ª tentativa', hint: 'Mude o canal se possível. Aguarde 2-3 dias.', color: 'warning' },
    2: { label: '3ª tentativa', hint: 'Tom mais curto. Card irá para Atrasado na próxima.', color: 'warning' },
    3: { label: '4ª tentativa', hint: 'Card em Atrasado. Faça mais uma antes do encerramento.', color: 'danger' },
    4: { label: 'Encerramento', hint: 'Envie mensagem de saída elegante e registre "Desistiu".', color: 'secondary' },
};
const getAttemptConfig = (attempts) => ATTEMPT_CONFIG[Math.min(attempts ?? 0, 4)];

const OperationalItemSidebar = ({ isOpen, toggle, item, onRefresh }) => {
    const { idTenant, user, tenantName } = useTenant();
    const [showHistoryModal, setShowHistoryModal] = useState(false);

    if (!item) return null;

    /* ── Derived badge data ───────────────────────────────────── */
    const reasonIcon = item.contactReason ? CONTACT_REASON_ICONS[item.contactReason] : null;
    const badgeColor = reasonIcon?.color || item.badgeInfo?.color || 'primary';
    const badgeText = item.contactReason ? (CONTACT_REASON_LABELS[item.contactReason] || item.badgeInfo?.text) : item.badgeInfo?.text;

    return (
        <Offcanvas isOpen={isOpen} toggle={toggle} direction="end" className="neo-sidebar-premium" style={{ width: "470px" }}>
            {/* ── HEADER ─────────────────────────────────────── */}
            <OffcanvasHeader toggle={toggle} className="pb-3 border-bottom-0">
                <span className="fw-bold font-size-16 neo-font-outfit text-brand-primary">
                    Detalhes da Tarefa
                </span>
            </OffcanvasHeader>

            <OffcanvasBody className="p-0 d-flex flex-column h-100 bg-white">
                <div className="flex-grow-1 p-4 custom-scrollbar overflow-auto">

                    {/* ── BADGES ROW ─────────────────────────── */}
                    <div className="d-flex align-items-center gap-2 mb-3 pb-3 border-bottom overflow-auto">
                        <NeoBadge color={badgeColor} icon={reasonIcon?.icon}>
                            {badgeText}
                        </NeoBadge>

                        {item.priority === TASK_PRIORITY.HIGH && (
                            <NeoBadge color="danger" icon="mdi-arrow-up-bold">
                                Alta Prioridade
                            </NeoBadge>
                        )}
                        {item.priority === TASK_PRIORITY.LOW && (
                            <NeoBadge color="success" icon="mdi-arrow-down-bold">
                                Baixa Prioridade
                            </NeoBadge>
                        )}

                        {/* Date + Info icon */}
                        <div className="ms-auto d-flex align-items-center gap-2">
                            {user?.role === 'owner' || user?.role === 'admin' ? (
                                <div className="d-flex align-items-center rounded-3 bg-light border px-2 py-1">
                                    <i className="mdi mdi-clock-outline me-1 text-accent font-size-14"></i>
                                    <input
                                        type="date"
                                        className="border-0 bg-transparent outline-none fw-bold text-brand-secondary font-size-11 p-0 m-0"
                                        style={{ width: '105px', cursor: 'pointer' }}
                                        value={item.date || ''}
                                        onChange={async (e) => {
                                            const newDate = e.target.value;
                                            if (newDate && newDate !== item.date) {
                                                try {
                                                    await TaskService.updateTaskDate(idTenant, user, item.id, newDate, item);
                                                    toast.success("Data atualizada com sucesso");
                                                    if (onRefresh) onRefresh();
                                                } catch (err) {
                                                    toast.error("Erro ao atualizar data");
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            ) : (
                                <span className="d-flex align-items-center rounded-3 bg-light border px-2 py-1 fw-bold text-brand-secondary font-size-11">
                                    <i className="mdi mdi-clock-outline me-1 text-accent"></i>
                                    {item.date ? item.date.split("-").reverse().join("/") : ""}
                                </span>
                            )}
                            <NeoButton
                                variant="link"
                                className="p-0 border-0 text-brand-secondary"
                                title="Ver histórico do card"
                                onClick={() => setShowHistoryModal(true)}
                                size="sm"
                                icon="mdi-information-outline"
                            />
                        </div>
                    </div>

                    {/* ── TITLE ───────────────────────────────── */}
                    <h4 className="fw-bold mb-3 neo-font-outfit text-brand-primary">
                        {item.title}
                    </h4>

                    {/* ── ATTEMPTS BADGE ──────────────────────── */}
                    {(item.contactAttempts > 0) && (
                        <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
                            <NeoBadge color={getAttemptConfig(item.contactAttempts).color} icon="mdi-phone-outline" title={getAttemptConfig(item.contactAttempts).hint}>
                                {getAttemptConfig(item.contactAttempts).label}
                            </NeoBadge>
                            {item.contactOutcome && (
                                <NeoBadge color="muted">
                                    Último: {item.contactOutcome}
                                </NeoBadge>
                            )}
                        </div>
                    )}

                    {/* ── DESCRIPTION ─────────────────────────── */}
                    {item.description && (
                        <NeoCard thinBorder={true} className="mb-4 mt-1 bg-light border-0 shadow-none" bodyClassName="p-3">
                            <MarkdownViewer
                                text={item.description.replace(/⭐/g, '').replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, "$3-$2-$1")}
                                className="font-size-13 text-brand-secondary"
                            />
                        </NeoCard>
                    )}

                    {/* ═══ CLIENTE VINCULADO ══════════════════ */}
                    <SidebarMemberInfo item={item} onClose={toggle} />

                    {/* ═══ CONSULTOR RESPONSÁVEL ═══════════════ */}
                    <SidebarConsultantInfo assignedEmployees={item.assignedEmployees} />

                    {/* ═══ COACH DE MENSAGENS (IA) ════════════ */}
                    <SidebarAICoachChat 
                        item={item} 
                        idTenant={idTenant} 
                        user={user} 
                        tenantName={tenantName} 
                    />

                    {/* ═══ CRM: REGISTRAR RESULTADO ═══════════ */}
                    <SidebarRegisterOutcome 
                        item={item} 
                        idTenant={idTenant} 
                        user={user} 
                        onRefresh={onRefresh} 
                    />

                    {/* ═══ HISTÓRICO DE CONTATOS ══════════════ */}
                    <SidebarContactHistory outcomeHistory={item.outcomeHistory} />

                    {/* ═══ ANOTAÇÕES ═════════════════════════ */}
                    <SidebarCardNotes 
                        item={item} 
                        idTenant={idTenant} 
                        user={user} 
                        onRefresh={onRefresh} 
                    />

                </div>
            </OffcanvasBody>

            {/* ═══ MODAL: HISTÓRICO DE ALTERAÇÕES ═══════════════ */}
            <SidebarHistoryModal 
                isOpen={showHistoryModal} 
                toggle={() => setShowHistoryModal(false)} 
                statusHistory={item.statusHistory} 
            />
        </Offcanvas>
    );
};

export default OperationalItemSidebar;
